import React, { ChangeEvent, useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import clsx from 'clsx';
import useFormatPrice from '@hooks/use-format-price-hook';
import Button from '@components/atoms/button/button';
import { SuggestedOffers } from '@components/organisms/negotiation/negotiation-modal/suggested-offers/suggested-offers';
import { debounce } from '@helpers/utils/general';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { useNegotiation } from '@context/negotiation.context';
import { useGetPriceDetails } from '@hooks/negotiations/useNegotationQueries';
import Spinner from '@components/atoms/spinner/spinner';
import { Formik, Form, Field, FormikProps } from 'formik';
import * as Yup from 'yup';
import styles from './negotiation-form.module.scss';

type NegotiationFormProps = {
  isDisabled: boolean;
  action: string;
  currency: string;
  language: string;
  min: number;
  max: number;
  onCancel?: () => void;
  onSubmit: (price: number) => void;
  productId: string;
  isSeller: boolean;
};

type FormValues = {
  offer: number | '';
};

const NegotiationForm: React.FC<NegotiationFormProps> = ({
  isDisabled = false,
  action,
  currency,
  language,
  min,
  max,
  onSubmit,
  onCancel,
  productId,
  isSeller,
}) => {
  const { t } = useTranslation();
  const { initialData } = useNegotiation();
  const { sendAnalyticEvent } = useAnalyticEvents('mmao');
  const [debouncePrice, setDebouncePrice] = useState<number>(0);
  const [isLoadingYouEarn, setIsLoadingYouEarn] = useState<boolean>(false);
  const [suggestedOfferIndex, setSuggestedOfferIndex] = useState<number | null>(null);
  const formikRef = useRef<FormikProps<FormValues>>(null);

  const suggestedOffers = initialData?.suggestedOffers;
  const { formatPrice } = useFormatPrice();
  const { data } = useGetPriceDetails(debouncePrice, productId);
  const placeholder = useMemo<string>(() => {
    if (action === 'new') {
      return 'MMAO.POPIN.OTHER_MSG.YOUR_OFFER';
    }
    return 'MMAO.POPIN.OTHER_MSG.YOUR_COUNTER_OFFER';
  }, [action]);

  const isSuggestedOffersShown = !isSeller && suggestedOffers && suggestedOffers.length > 0;

  const trackOfferSubmit = () => {
    const offerType = suggestedOfferIndex === null ? 'custom' : 'suggested';
    const suggestedOfferPercent = (suggestedOffers?.[suggestedOfferIndex]?.offerLabel || '').replace('%', '');
    sendAnalyticEvent('mmao_send_an_offer', {
      property: offerType,
      value: suggestedOfferPercent,
    });
  };

  const handleSubmit = async (formValues: FormValues) => {
    if (formikRef?.current.isValid) {
      if (formValues.offer === '') {
        return;
      }
      onSubmit(formValues.offer);
      trackOfferSubmit();
    }
  };

  const onKeyDown = () => {
    setIsLoadingYouEarn(true);
  };

  const onChangePriceEvent = debounce((e: ChangeEvent<HTMLInputElement>) => {
    if (!Boolean(e.target.value) || e.target.value === debouncePrice.toString()) {
      setIsLoadingYouEarn(false);
      return;
    } else if (formikRef.current?.isValid) {
      setDebouncePrice(Number(e.target.value));
    }
    sendAnalyticEvent('mmao_send_an_offer_error', {
      label: Number(e.target.value) < min ? 'offer_value_too_low' : 'offer_value_too_high',
      property: String(e.target.value),
    });
  }, 500);

  useEffect(() => {
    if (data?.pvn_with_symbol) {
      setIsLoadingYouEarn(false);
    }
  }, [data, formikRef.current?.isValid]);

  const setOfferValue = (offerPrice: number) => {
    if (formikRef.current) {
      formikRef.current.setFieldValue('offer', offerPrice);
    }
  };

  const negotiationSchema = Yup.object().shape({
    offer: Yup.number()
      .min(
        min,
        t('MMAO.POPIN.FORM.LOWER_BOUND.ERROR', {
          amount: formatPrice(min, currency, language),
        }),
      )
      .max(
        max,
        t('MMAO.POPIN.FORM.UPPER_BOUND.ERROR', {
          amount: formatPrice(max, currency, language),
        }),
      )
      .required(''),
  });

  const handleSuggestedOfferOnChange = (selectedOfferIndex: number) => {
    if (formikRef.current) {
      formikRef.current.setFieldTouched('offer', false);
      if (suggestedOffers?.[selectedOfferIndex]?.offerAmount) {
        setOfferValue(suggestedOffers?.[selectedOfferIndex]?.offerAmount?.cents / 100);
      } else {
        setOfferValue(0);
      }
      setSuggestedOfferIndex(selectedOfferIndex);
    }
  };

  useEffect(() => {
    // set initial value for suggested offer
    if (initialData?.suggestedOffers) {
      const recommendedIndex = initialData?.suggestedOffers?.findIndex((offer) => offer.recommended);
      setSuggestedOfferIndex(recommendedIndex);
      const initialAmountInCents = initialData?.suggestedOffers?.[recommendedIndex]?.offerAmount?.cents;
      if (initialAmountInCents) {
        setOfferValue(initialAmountInCents / 100);
      }
    }
  }, [initialData]);

  return (
    <>
      {['new', 'offer'].includes(action) && (
        <Formik
          innerRef={formikRef}
          initialValues={{ offer: '' }}
          validationSchema={negotiationSchema}
          onSubmit={handleSubmit}
        >
          {({ setFieldTouched, setFieldValue, handleSubmit: formikHandleSubmit, touched, values, isValid, errors }) => (
            <Form onSubmit={formikHandleSubmit}>
              {isSuggestedOffersShown && (
                <SuggestedOffers
                  suggestedOffers={initialData?.suggestedOffers}
                  value={suggestedOfferIndex}
                  onChange={handleSuggestedOfferOnChange}
                  displayPricingInsight={initialData?.product?.displayPricingInsight}
                />
              )}
              <div
                className={clsx(
                  `vc-formUnit roundedCorners roundedCorners--withIcon ${styles.mmao__offer__input} ${
                    !isValid && touched.offer ? 'error' : ''
                  }`,
                  isSuggestedOffersShown
                    ? styles['mmao__offer__input--hasSuggestedOffer']
                    : styles['mmao__offer__input--noSuggestedOffer'],
                )}
              >
                <Field
                  name="offer"
                  id="offer-price"
                  style={{ height: '48px' }}
                  aria-describedby={errors.offer ? 'offer-price-error' : undefined}
                  data-cy="offer_price_input"
                  className="vc-inputText"
                  aria-invalid={!isValid && touched.offer ? 'true' : 'false'}
                  type="number"
                  min={min}
                  max={max}
                  step="1"
                  placeholder={t(placeholder)}
                  disabled={isDisabled}
                  onKeyDown={(e) => {
                    if (/^[0-9]+$/.test(e.key) || ['Backspace', 'Delete'].includes(e.key)) {
                      onKeyDown();
                      setFieldTouched('offer');
                    } else if (['ArrowRight', 'ArrowLeft', 'Tab'].includes(e.key)) {
                      return true;
                    } else {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    onChangePriceEvent(e);
                    setFieldValue('offer', e.target.value);
                  }}
                  onFocus={() => {
                    if (!values.offer) {
                      setFieldValue('offer', '');
                      setSuggestedOfferIndex(null);
                    } else {
                      setFieldTouched('offer');
                    }
                  }}
                />
                <label
                  className="vc-inputText-label"
                  htmlFor="offer-price"
                >
                  {t(placeholder)}
                </label>
                {isDisabled ? (
                  <span
                    className="vc-inputText-errorMsg"
                    id="offer-price-error"
                    role="alert"
                  >
                    {t('FAVORITE_PAGE.PRODUCT_TILE.MMAO_SHIPPING_DISABLED')}
                  </span>
                ) : !isValid && touched.offer ? (
                  <span
                    id="offer-price-error"
                    className={`${styles.mmao__offer__input__message} vc-inputText-errorMsg`}
                    data-cy="mmao_error"
                    role="alert"
                  >
                    {errors.offer}
                  </span>
                ) : (
                  isSeller && (
                    <div className={styles.mmao__offer__input__message}>
                      {`${t('MMAO.SELLER.YOU_EARN')}: `}
                      {isLoadingYouEarn && values.offer && isValid ? (
                        <Spinner className={styles.mmao__offer__input__message__spinner} />
                      ) : values.offer && data?.pvn_with_symbol ? (
                        data.pvn_with_symbol
                      ) : (
                        '-'
                      )}
                    </div>
                  )
                )}
              </div>
              <div
                className={clsx('clearfix', styles.mmao__offer__buttonGroup)}
                data-cy="mmao_action"
              >
                <>
                  {action === 'offer' && (
                    <Button
                      variant="secondary"
                      size="large"
                      onClick={() => onCancel?.()}
                    >
                      {t('MMAO.CTA.CANCEL')}
                    </Button>
                  )}
                  <Button
                    type="submit"
                    variant="primary"
                    size="large"
                    fullWidth
                    disabled={!isValid || !values.offer}
                    iconLeft="send-large"
                  >
                    {t('MMAO.CTA.SEND', {
                      amount: values.offer && isValid ? `${formatPrice(values.offer, currency, language)}` : '',
                    })}
                  </Button>
                </>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};

export default NegotiationForm;
